import { netAmount, shortDate } from "@/lib/format";
import { colors } from "@/lib/theme";
import type { InvestorTrendPoint } from "@/lib/types";
import { useElementWidth } from "@/lib/useElementWidth";

const HEIGHT = 150;
const PAD_TOP = 8;
const PAD_BOTTOM = 18;
const MAX_POINTS = 20;

type SeriesKey = "individual" | "foreign" | "institution";

const SERIES: { key: SeriesKey; label: string; color: string }[] = [
  { key: "individual", label: "개인", color: colors.warn },
  { key: "foreign", label: "외국인", color: colors.accent },
  { key: "institution", label: "기관", color: colors.buy },
];

function qtyOf(p: InvestorTrendPoint, key: SeriesKey): number {
  const v = {
    individual: p.individual_net_qty,
    foreign: p.foreign_net_qty,
    institution: p.institution_net_qty,
  }[key];
  return v ?? 0;
}

/**
 * 일자별 투자자(개인/외국인/기관) 순매수 수량을 그룹 막대로 표시.
 * 0선 기준 위=순매수, 아래=순매도.
 */
export function InvestorTrendChart({ points }: { points: InvestorTrendPoint[] }) {
  const [ref, width] = useElementWidth();
  const shown = points.slice(-MAX_POINTS);

  if (shown.length === 0) {
    return (
      <div className="chart" ref={ref}>
        <p className="hint">투자자 매매동향 데이터가 없습니다.</p>
      </div>
    );
  }

  const maxAbs = Math.max(
    1,
    ...shown.flatMap((p) => SERIES.map((s) => Math.abs(qtyOf(p, s.key)))),
  );
  const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;
  const zeroY = PAD_TOP + plotH / 2;
  const slotW = width > 0 ? width / shown.length : 0;
  const groupGap = 2;
  const barW = slotW > 0 ? Math.max((slotW - groupGap) / SERIES.length - 1, 1) : 1;
  const scale = (v: number) => (v / maxAbs) * (plotH / 2);

  const last = shown[shown.length - 1];

  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label="투자자별 순매수 수량 추이"
        >
          {/* 0 기준선 */}
          <line
            x1={0}
            y1={zeroY}
            x2={width}
            y2={zeroY}
            stroke={colors.cardBorder}
            strokeWidth={1}
          />
          {shown.map((p, i) =>
            SERIES.map((s, j) => {
              const v = qtyOf(p, s.key);
              const h = Math.abs(scale(v));
              return (
                <rect
                  key={`${p.date}-${s.key}`}
                  x={i * slotW + groupGap / 2 + j * (barW + 1)}
                  y={v >= 0 ? zeroY - h : zeroY}
                  width={barW}
                  height={Math.max(h, 0.5)}
                  fill={s.color}
                  opacity={0.9}
                />
              );
            }),
          )}
        </svg>
      )}

      <div className="chart-axis">
        <span>{shortDate(shown[0].date)}</span>
        <span>{shortDate(last.date)}</span>
      </div>

      <div className="legend">
        {SERIES.map((s) => (
          <span className="legend-item" key={s.key}>
            <span className="legend-dot" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>

      <p className="amt-label">최신 순매수대금(원)</p>
      <p className="amt-value">
        개인 {netAmount(last.individual_net_amt)} · 외국인{" "}
        {netAmount(last.foreign_net_amt)} · 기관{" "}
        {netAmount(last.institution_net_amt)}
      </p>
    </div>
  );
}
